/**
 * The active-company "session layer" the company-scoped services expect.
 *
 * Company-scoped backend callables (recruitment, etc.) require a `companyId` the
 * signed-in user is a member of. Before this bridge, services hardcoded a
 * `co_demo` placeholder and called the backend at app startup — which the server
 * correctly rejected (400 missing / 403 not-a-member), spamming the console
 * even though the mock fallback kept the UI working.
 *
 * `CompanyProvider` sets the real active company id here; services read it and
 * SKIP the backend entirely (mock only) when there is no active company, so no
 * doomed request is ever sent.
 */
import { withBackend } from '@/services/api/backend';

let currentCompanyId: string | null = null;

export const setCompanyScope = (id: string | null) => { currentCompanyId = id; };
export const getCompanyScope = (): string | null => currentCompanyId;

/**
 * Call a company-scoped callable with the active company id injected. When there
 * is no active company, returns the mock directly WITHOUT hitting the backend,
 * so no rejected request reaches the console.
 */
export function scopedCall<T>(
  name: string,
  payload: Record<string, unknown>,
  mock: () => Promise<T>,
): Promise<T> {
  const companyId = getCompanyScope();
  if (!companyId) return mock();
  return withBackend<T>(name, { ...payload, companyId }, mock);
}
